import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth, fileUrl } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Play, LogOut, CheckCircle2, Clock, HeartPulse, IdCard, UserCog } from "lucide-react";

export default function MyTrainings() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const { branding } = useBranding();
  const brandColor = branding?.theme_color || "#0EA5E9";

  useEffect(() => { api.get("/employee/my-trainings").then(r => setData(r.data)).catch(()=>{}); }, []);

  function logout() { auth.clear(); nav("/employee/login"); }

  function start(item) {
    // Bridge to public quiz entry — we'll auto-fill emp_id/email there
    sessionStorage.setItem("emp_prefill", JSON.stringify({ emp_id: data.employee.emp_id, email: data.employee.email }));
    nav(`/q/${item.token}`);
  }

  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const completedCount = data.items.filter(i => i.completed).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {branding?.logo_path ? (
              <img src={fileUrl(branding.logo_path)} alt="" className="h-10 w-10 rounded-xl object-cover"/>
            ) : (
              <div className="h-10 w-10 rounded-xl grid place-items-center text-white" style={{ background: brandColor }}>
                <HeartPulse className="h-5 w-5"/>
              </div>
            )}
            <div>
              <div className="font-display font-black leading-tight">{branding?.hospital_name || "MedLearn"}</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">My Trainings</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={()=>nav("/profile")} data-testid="emp-profile-btn">
              <UserCog className="h-4 w-4 mr-1.5"/>Profile
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="emp-logout"><LogOut className="h-4 w-4 mr-1.5"/>Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-5 space-y-5">
        <Card className="border-0 rounded-3xl overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor}cc)` }}>
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-[0.22em] text-white/80 font-bold">Hello,</div>
            <h1 className="font-display font-black text-3xl mt-1">{data.employee.name}</h1>
            <div className="text-sm text-white/80 mt-1">{data.employee.emp_id} · {data.employee.email}</div>
            <div className="flex gap-3 mt-4 items-center">
              <Stat label="Assigned" value={data.items.length}/>
              <Stat label="Completed" value={completedCount}/>
              <Stat label="Pending" value={data.items.length - completedCount}/>
              <Button onClick={()=>nav("/my/card")} className="ml-auto bg-white/15 hover:bg-white/25 backdrop-blur text-white border border-white/20" data-testid="my-card-btn">
                <IdCard className="h-4 w-4 mr-1.5"/>Training Card
              </Button>
            </div>
          </CardContent>
        </Card>

        <h2 className="font-display font-bold text-xl">Your Trainings</h2>
        {data.items.length === 0 && (
          <Card className="border-slate-200 rounded-2xl"><CardContent className="p-8 text-center text-slate-500">No trainings assigned to you yet. Please check back later!</CardContent></Card>
        )}
        {data.items.map(i => {
          const pct = i.best_score != null && i.max_score ? Math.round((i.best_score/i.max_score)*100) : null;
          return (
            <Card key={i.questionnaire_id} className="border-slate-200 rounded-2xl overflow-hidden">
              <div className="flex flex-col sm:flex-row">
                <div className="sm:w-40 aspect-[16/10] sm:aspect-auto bg-slate-100 flex-shrink-0">
                  {i.cover_image ? <img src={i.cover_image.startsWith("http") ? i.cover_image : fileUrl(i.cover_image)} alt="" className="w-full h-full object-cover"/> : null}
                </div>
                <CardContent className="p-5 flex-1">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold" style={{ color: brandColor }}>{i.topic || "Training"}</div>
                      <h3 className="font-display font-bold text-lg mt-1">{i.title}</h3>
                      <div className="text-xs text-slate-500 mt-1">{i.num_questions} questions</div>
                    </div>
                    {i.completed ? (
                      <Badge className="bg-green-100 text-green-700 border-0"><CheckCircle2 className="h-3 w-3 mr-1"/>Completed</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1"/>Pending</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    {pct != null ? (
                      <div className="text-sm"><span className="text-slate-500">Best: </span><span className="font-bold">{i.best_score}/{i.max_score} ({pct}%) <Trophy className="h-4 w-4 inline" style={{ color: brandColor }}/></span></div>
                    ) : <div className="text-sm text-slate-400">Not attempted</div>}
                    <Button onClick={() => start(i)} style={{ background: brandColor }} className="text-white hover:opacity-90" data-testid={`start-${i.questionnaire_id}`}>
                      <Play className="h-4 w-4 mr-1.5"/>{i.completed ? "Retake" : "Start"}
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/15 backdrop-blur rounded-2xl px-4 py-2 text-center min-w-[70px]">
      <div className="text-2xl font-display font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/80 font-bold mt-0.5">{label}</div>
    </div>
  );
}
